import { useCallback, useEffect, useState } from "react";

/**
 * useFullscreen — wraps the Fullscreen API into a single state + toggle.
 *
 *   const { isFullscreen, supported, enter, exit, toggle } = useFullscreen();
 *
 * Notes:
 *   • iOS Safari only partially supports the API on documentElement; on
 *     iPhones the recommended path is "Add to Home Screen" (PWA install).
 *     We still expose toggle() because iOS 16.4+ does work for many cases.
 *   • Android Chrome and all desktop browsers fully support this.
 *   • `supported` is true if ANY of the vendor APIs are available so we
 *     can hide the UI button entirely on browsers that have nothing.
 */
export function useFullscreen() {
  const el = () => document.documentElement;

  const getFsElement = () =>
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null;

  const [isFullscreen, setIsFullscreen] = useState(() => !!getFsElement());

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!getFsElement());
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    document.addEventListener("mozfullscreenchange", onChange);
    document.addEventListener("MSFullscreenChange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      document.removeEventListener("mozfullscreenchange", onChange);
      document.removeEventListener("MSFullscreenChange", onChange);
    };
  }, []);

  const enter = useCallback(async () => {
    const e = el();
    const req =
      e.requestFullscreen ||
      e.webkitRequestFullscreen ||
      e.mozRequestFullScreen ||
      e.msRequestFullscreen;
    if (!req) return false;
    try {
      await req.call(e);
      return true;
    } catch (_e) {
      return false;
    }
  }, []);

  const exit = useCallback(async () => {
    const fn =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;
    if (!fn) return false;
    try {
      await fn.call(document);
      return true;
    } catch (_e) {
      return false;
    }
  }, []);

  const toggle = useCallback(() => {
    return isFullscreen ? exit() : enter();
  }, [isFullscreen, enter, exit]);

  const supported = !!(
    document.documentElement.requestFullscreen ||
    document.documentElement.webkitRequestFullscreen ||
    document.documentElement.mozRequestFullScreen ||
    document.documentElement.msRequestFullscreen
  );

  // Detect installed-PWA mode — already fullscreen at the OS level so we
  // hide the prompt + menu item to avoid confusion.
  const isStandalonePWA =
    (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches) ||
    (typeof window !== "undefined" && window.navigator?.standalone === true);

  return { isFullscreen, supported, enter, exit, toggle, isStandalonePWA };
}

/**
 * useIsIosSafari — true ONLY when running in iOS Safari (NOT Chrome iOS,
 * NOT in a PWA shell). Used to gate the "Add to Home Screen" hint banner
 * since iOS still has limited Fullscreen API support and the PWA install
 * path is the most reliable way to hide browser chrome on iPhones.
 */
export function useIsIosSafari() {
  const [is, setIs] = useState(false);
  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator?.standalone === true;
    setIs(isIos && isSafari && !isStandalone);
  }, []);
  return is;
}
